import React, { Fragment, useEffect, useState } from "react";
import { connect } from "react-redux";
import { arrayOf, bool, func, shape, string } from "prop-types";
import { routerActions } from "connected-react-router";
import * as i18n from "util/i18n";
import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";

import Notification from "pages/_components/Notification";
import MainContainer from "pages/_components/MainContainer";
import GridTable from "pages/_components/GridTable/GridTable";

import CustomProductHead from "pages/_components/CustomProductHead";
import ViewTitle from "pages/_components/header/components/ViewTitle";

import Credential from "pages/_components/fields/credentials/Credential";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import Button from "pages/_components/Button";
import { TransactionInfo } from "pages/_components/TransactionCard";
import gaEventTracker, { events } from "util/gaEventTracker";

const NewAccountSummaryForm = ({
    dispatch,
    fetching,
    newAccountData,
    newAccountRequiredCredentials,
    newAccountTicketStatus,
    newAccountTicketId,
}) => {
    const _modalities = newAccountData?.modalities?.filter((modality) => modality !== "OTHER");
    const isLoading = fetching || !newAccountData?.purpose?.length || !newAccountRequiredCredentials?.length;
    const credentialType = newAccountRequiredCredentials.includes("otp") ? "otp" : "password";
    const [isValidating, setIsValidating] = useState(true);
    const [formRef, setFormRef] = useState();

    useEffect(() => {
        dispatch(accountsActions.fetchCredentialGroup());
    }, [dispatch]);

    useEffect(() => {
        if (isValidating && newAccountTicketStatus === "INVALID_CREDENTIAL") {
            setIsValidating(false);
            formRef.setErrors({
                credential: i18n.get(
                    credentialType === "password"
                        ? "preferences.password.password.incorrect"
                        : "login.step1.secondFactor.invalid",
                ),
            });
        } else if (newAccountTicketStatus === "PENDING" && newAccountTicketId?.length) {
            dispatch(routerActions.push(`/accounts/pendingTransaction/${newAccountTicketId}`));
        } else if (newAccountTicketStatus === "FINISHED" && newAccountTicketId?.length) {
            dispatch(routerActions.push(`/accounts/successTransaction/${newAccountTicketId}`));
        }

    }, [isValidating, formRef, newAccountTicketStatus, newAccountTicketId]);

    const handleSubmit = ({ credential }) => {
        setIsValidating(true);
        // gaEventTracker.triggerEvent(events.accountRequest2, {
        //     productType: newAccountData?.account?.productType,
        //     periodicity: newAccountData?.periodicity,
        // });
        // gaEventTracker.triggerEvent(
        //     events.newAccount({
        //         productType: newAccountData?.account?.productType,
        //         periodicity: newAccountData?.periodicity,
        //         destination:
        //             newAccountData?.purpose === "OTHER" ? newAccountData?.purposeReason : newAccountData?.purpose,
        //         modalities: newAccountData?.modalities,
        //     }),
        // );

        if (newAccountData?.idTransaction) {
            gaEventTracker.triggerEvent(events.cuentas_solicitud_firmar());
            dispatch(
                accountsActions.setNewAccountCredential({
                    idTransaction: newAccountData?.idTransaction,
                    [`_${credentialType}`]: credential,
                }),
            );
        } else {
            gaEventTracker.triggerEvent(events.cuentas_solicitud_enviar());
            dispatch(
                accountsActions.setNewAccountCredential({
                    ...newAccountData,
                    [`_${credentialType}`]: credential,
                }),
            );
        }
    };

    const handleBack = () => {
        dispatch(accountsActions.newAccountDataShouldShowLastValues(true));
        dispatch(routerActions.goBack());
    };

    return (
        <Fragment>
            <Notification scopeToShow="accounts" />
            <CustomProductHead titleId="accounts.create.title" onBack={handleBack}>
                <div className="toolbar-item view-title">
                    <ViewTitle id="accounts.create.title" />
                </div>
            </CustomProductHead>
            <MainContainer showLoader={isLoading}>
                <div className="container">
                    <h2 className="text-lead" style={{ fontWeight: 500 }}>
                        {i18n.get("accounts.create.summaryTitle")}
                    </h2>
                    <GridTable className="deposit-summary form-summary">
                        <GridTable.Container>
                            <GridTable.Data className="accountType">
                                <TransactionInfo
                                    field="accounts.create.accountType"
                                    i18nId={`accounts.create.accountType.${newAccountData?.account?.productType}`}
                                />
                            </GridTable.Data>
                            <GridTable.Data className="periodicity">
                                <TransactionInfo
                                    field="accounts.create.periodicity"
                                    i18nId={`accounts.create.periodicity.${newAccountData?.periodicity}`}
                                />
                            </GridTable.Data>
                            <GridTable.Data className="modalities">
                                <div className="custom-data">
                                    <span className="custom-text custom-data-title">
                                        {i18n.get("accounts.create.modalities")}
                                    </span>
                                    <div>
                                        {_modalities?.map((modality, index) => (
                                            <span className="custom-text custom-data-value " key={modality}>
                                                {!!index && ", "}
                                                {i18n.get(`accounts.create.modality.${modality}`)}
                                            </span>
                                        ))}
                                        {!!newAccountData?.modalityReason?.length && (
                                            <span className="custom-text custom-data-value ">
                                                {!!_modalities?.length && ", "}
                                                {newAccountData?.modalityReason}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </GridTable.Data>
                            <GridTable.Data className="purpose">
                                <TransactionInfo field="accounts.create.purpose">
                                    {newAccountData?.purpose === "OTHER"
                                        ? newAccountData?.purposeReason
                                        : i18n.get(`accounts.create.purpose.${newAccountData?.purpose}`)}
                                </TransactionInfo>
                            </GridTable.Data>
                            <GridTable.Data className="messageToBank">
                                <TransactionInfo
                                    field="accounts.create.messageToBank"
                                    value={newAccountData?.messageToBank}
                                />
                            </GridTable.Data>
                        </GridTable.Container>
                    </GridTable>
                    <Formik
                        ref={(el) => setFormRef(el)}
                        validateOnBlur
                        validateOnChange={false}
                        initialValues={{
                            credential: "",
                        }}
                        validationSchema={Yup.object().shape({
                            credential: Yup.string().required(i18n.get("deposits.request.required")),
                        })}
                        onSubmit={handleSubmit}>
                        {({ isSubmitting }) => (
                            <Form>
                                <GridTable className="grid-table-buttons">
                                    <GridTable.Container>
                                        <GridTable.Data columnStart={3} columnWidth={4}>
                                            <Field
                                                idForm="form.credential"
                                                name="credential"
                                                labelText={`form.credential.${credentialType}.label`}
                                                placeholderText={
                                                    credentialType === "otp" ? "Ej: 120573" : "Ej: Ju@n_123"
                                                }
                                                component={Credential}
                                                type={credentialType}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={7} columnWidth={4}>
                                            <div className="form-group">
                                                <div className="form-group">
                                                    <span className="data-label">
                                                        <span> &nbsp;</span>
                                                    </span>
                                                    <div className="button-group">
                                                        <Button
                                                            block
                                                            type="submit"
                                                            label="global.send"
                                                            bsStyle="primary"
                                                            loading={isSubmitting && isValidating}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </GridTable.Data>
                                    </GridTable.Container>
                                </GridTable>
                            </Form>
                        )}
                    </Formik>
                </div>
            </MainContainer>
        </Fragment>
    );
};

NewAccountSummaryForm.propTypes = {
    newAccountData: shape({}).isRequired,
    newAccountTicketStatus: string,
    newAccountTicketId: string,
    newAccountRequiredCredentials: arrayOf(string).isRequired,
    dispatch: func.isRequired,
    fetching: bool,
};

NewAccountSummaryForm.defaultProps = {
    newAccountTicketStatus: "",
    newAccountTicketId: "",
    fetching: false,
};

const mapStateToProps = (state) => ({
    newAccountData: accountsSelectors.getNewAccountData(state),
    newAccountRequiredCredentials: accountsSelectors.getNewAccountRequiredCredentials(state),
    newAccountTicketStatus: accountsSelectors.getNewAccountTicketStatus(state),
    newAccountTicketId: accountsSelectors.getNewAccountTicketId(state),
    fetching: accountsSelectors.getFetching(state),
});

export default connect(mapStateToProps)(NewAccountSummaryForm);
